import { useEffect, useState } from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { FaAngleDown } from "react-icons/fa";
import { Button, Grid, Stack, useMediaQuery } from "@mui/material";
import AuthService from "services/auth.service";
import OneSignalService from "services/onesignal.service";
import { useAppSelector } from "redux/hooks";
import {
  fleetList,
  usersList,
  financeList,
  supportList,
  eventsList,
  systemList,
  invoiceList,
  deliveryList,
} from ".";
import Theme from "theme";
import BlockRender from "components/organisms/BlockRender";
import logo from "assets/yeloplus-logo.png";
import "./Header.css";
import SocketService from "services/socket.service";

// Menu item structure
interface MenuItem {
  text: string;
  to: string;
}

interface GroupedMenus {
  [key: string]: MenuItem[];
}

export default function Header() {
  // const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const routes = useAppSelector((state) => state.auth?.user?.role_id?.routes);
  const isMobile = useMediaQuery(Theme.breakpoints.down("md"));
  const [groupedMenus, setGroupedMenus] = useState<GroupedMenus>({});

  useEffect(() => {
    if (user) {
      OneSignalService.connect(user._id);
      SocketService.connect(user);
    }
  }, [user]);

  useEffect(() => {
    if (typeof routes !== "object") return;

    const filterByRoutes = (list: MenuItem[]) =>
      list.filter((item) => routes.includes(item.to));

    setGroupedMenus({
      Fleet: filterByRoutes(fleetList),
      Users: filterByRoutes(usersList),
      Finance: filterByRoutes(financeList),
      Invoices: filterByRoutes(invoiceList),
      Support: filterByRoutes(supportList),
      Promotions: filterByRoutes(eventsList),
      System: filterByRoutes(systemList),
      Delivery: filterByRoutes(deliveryList),
    });
  }, [routes]);

  // Don't render header on root path or if no user (logged-in branch map uses the shell)
  if (!user || pathname === "/") return null;

  // Function to render menu
  const renderMenu = (label: string, items: MenuItem[], index: number) => {
    const isActive = items.some((item) => item.to === pathname);

    return (
      <div className="menu-item" key={index}>
        <Stack direction="row" alignItems="center" columnGap={1}>
          <span
            className="memu-item-label"
            style={{
              fontWeight: isActive ? 600 : "normal",
              color: isActive ? "#f5d312" : "white",
            }}
          >
            {label}
          </span>
          <FaAngleDown
            className="memu-item-icon"
            color={isActive ? "#f5d312" : "white"}
          />
        </Stack>
        <div className="dropdown">
          <ul>
            {items.map(({ text, to }) => (
              <li key={text}>
                <NavLink to={to}>{text}</NavLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  // Function for rendering user menu
  const renderUserMenu = () => (
    <div
      className="menu-item"
      style={{ background: "white", padding: "12px", borderRadius: "50%" }}
    >
      <span style={{ color: "#520A76" }}>{`${user.first_name?.[0] || ""}${
        user.last_name?.[0] || ""
      }`}</span>
      <div className="dropdownAvatar">
        <ul>
          <li onClick={() => AuthService.logout()}>Logout</li>
        </ul>
      </div>
    </div>
  );

  // Function to render navigation links
  const renderNavLinks = () => (
    <>
      <BlockRender roles="/dashboard">
        <NavLink to="/dashboard">Dashboard</NavLink>
      </BlockRender>
      <BlockRender roles="/revenue-report">
        <NavLink to="/revenue-report">Revenue Report</NavLink>
      </BlockRender>
      <BlockRender roles="/bookings">
        <NavLink to="/bookings">Bookings</NavLink>
      </BlockRender>
      {Object.entries(groupedMenus).map(
        ([group, items], index) =>
          items.length > 0 && renderMenu(group, items, index)
      )}
    </>
  );

  // Desktop header rendering
  const renderDesktopHeader = () => (
    <div className="navbar">
      <div className="nav-wrapper">
        <div className="logo">
          <NavLink to="/bookings">
            <img src={logo} alt="logo" width="70px"  />
          </NavLink>
        </div>
        <div className="nav-center">
          <div className="ILinks">{renderNavLinks()}</div>
        </div>
        <div className="nav-user">{renderUserMenu()}</div>
      </div>
    </div>
  );

  // Mobile header rendering
  const renderMobileHeader = () => (
    <div className="navbar">
      <Grid container alignItems="center">
        <Grid item xs={12} lg={2}>
          <NavLink to="/bookings">
            <img src={logo} alt="logo" width="70px" />
          </NavLink>
        </Grid>
        <Grid item xs={12} lg={10}>
          <div className="nav-wrapper">
            <div className="nav-center">
              <div className="ILinks">{renderNavLinks()}</div>
            </div>
            <div className="nav-user">{renderUserMenu()}</div>
          </div>
        </Grid>
      </Grid>
    </div>
  );

  return (
    <header>{isMobile ? renderMobileHeader() : renderDesktopHeader()}</header>
  );
}
